import { z } from 'zod';
import { listSearchParam } from '@/common/utils/listSearchParam';
import { findingGroups } from '@/features/loop-intelligence/data/types';

export { selectedValues, toListParam } from '@/common/utils/listSearchParam';

const loopIdSchema = z.coerce.number().int().positive();

/**
 * Which loop's run is being read, as the URL states it: absent means the loop navigated to, `'all'`
 * means every readable loop merged, one id means that loop specifically, and two or more mean the
 * merged reading narrowed to those loops. Never stored as a bare `number | null` in the URL — an
 * absent param has to mean "the nav loop", not "all loops", so the two need distinct spellings.
 * `loopScope.ts` turns this into what the rest of the page reasons about.
 */
const loopParamSchema = z
  .union([z.literal('all'), z.array(loopIdSchema), loopIdSchema])
  .optional()
  .catch(undefined);

export const loopIntelligenceTabs = ['new', 'assigned', 'completed'] as const;
export type LoopIntelligenceTab = (typeof loopIntelligenceTabs)[number];

export const assignLensValues = ['toyou', 'byyou'] as const;
export type AssignLens = (typeof assignLensValues)[number];

/**
 * The whole surface's state lives in the URL, so a link reproduces exactly what someone was looking
 * at — which run, which loop, filtered to which groups and objectives. Which findings are open is the
 * one deliberate exception: independent disclosures (any number of cards open at once) are tracked
 * as local component state in `FindingFeed` instead, so a shared link no longer reproduces which
 * cards were open — see `data/openFindingIds.ts`.
 *
 * Every field is `.catch(undefined)` rather than plain `.optional()`: these arrive from a
 * hand-editable address bar, and a value that does not resolve should fall back to the default rather
 * than throw a route error at someone who mistyped a link.
 */
export const loopIntelligenceSearchSchema = z.object({
  objective: listSearchParam(z.string().min(1)),
  group: listSearchParam(z.enum(findingGroups)),
  loop: loopParamSchema,
  tab: z.enum(loopIntelligenceTabs).optional().catch(undefined),
  /** `assignLens`, not `lens`: the classic surface already has an unrelated `?lens=` and the two
   * pages navigate into each other. */
  assignLens: z.enum(assignLensValues).optional().catch(undefined),
  /** Reserved for the notification deep-link. Nothing reads it yet. */
  assignment: z.string().min(1).optional().catch(undefined),
});

export type LoopIntelligenceSearch = z.output<typeof loopIntelligenceSearchSchema>;
/** What a navigation may write — the same shapes the URL can carry. */
export type LoopIntelligenceSearchInput = z.input<typeof loopIntelligenceSearchSchema>;
